import { useMemo } from 'react';
import { useGetReceivedPlayRequestsQuery } from 'types/generated/client';
import { buildGameTitle } from 'utils/shared/games/buildGameTitle';
import { useViewer } from 'hooks/useViewer';
import type { FindPlayersRequestSummary } from './FindPlayersSection';

// The `GetReceivedPlayRequests` document lives in ./graphql.ts as a codegen source;
// the generated `useGetReceivedPlayRequestsQuery` embeds it, so it isn't imported here.

/**
 * The viewer's incoming PENDING play requests, grouped per listing (one summary
 * row per game/availability) for the home "Requests" card. Multiple requesters on
 * the same listing collapse into a single row with a bumped `requestCount`.
 */
export function useReceivedRequests() {
  const viewer = useViewer();

  const { data, loading, called } = useGetReceivedPlayRequestsQuery({
    variables: { userId: viewer.userId ?? '' },
    skip: !viewer.userId,
    fetchPolicy: 'cache-and-network',
  });

  const requests = useMemo<FindPlayersRequestSummary[]>(() => {
    // Insertion order = request recency (query is createdAt DESC), so the newest
    // listing sits first.
    const byListing = new Map<string, FindPlayersRequestSummary>();

    for (const request of data?.playRequests ?? []) {
      // A request targets exactly one listing (XOR enforced by the DB CHECK).
      const listing = request.event
        ? {
            id: request.event.id,
            type: 'GAME' as const,
            sport: request.event.sport,
            source: request.event.groups[0],
          }
        : request.availability
        ? {
            id: request.availability.id,
            type: 'AVAILABILITY' as const,
            sport: request.availability.sport,
            source: request.availability,
          }
        : null;
      if (!listing) continue;

      const existing = byListing.get(listing.id);
      if (existing) {
        existing.requestCount += 1;
        continue;
      }

      byListing.set(listing.id, {
        id: listing.id,
        type: listing.type,
        sport: listing.sport,
        title: listing.source ? buildGameTitle(listing.source) : '',
        requestCount: 1,
      });
    }

    return Array.from(byListing.values());
  }, [data]);

  return { requests, loading: loading || !called };
}
